import React, { useEffect, useRef, useState } from 'react';
import type { DemoUser } from '@/data/users';

interface HeaderProps {
  onLogout?: () => void;
  onProfile?: () => void;
  viewAsUsers: DemoUser[];
  viewAsKey: string;
  onViewAsChange: (key: string) => void;
}

export const Header: React.FC<HeaderProps> = ({
  onLogout,
  onProfile,
  viewAsUsers,
  viewAsKey,
  onViewAsChange,
}) => {
  const active = viewAsUsers.find((u) => u.key === viewAsKey) ?? viewAsUsers[0];
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <header style={styles.header}>
      <div style={styles.container}>
        <div style={styles.leftSection}>
          <h1 style={styles.title}>HEVI-LOAD Co-Pilot</h1>
          <p style={styles.subtitle}>AI-Powered Energy and Transportation Analysis Platform</p>
        </div>
        <div style={styles.rightSection}>
          <div ref={wrapRef} style={styles.viewAsWrapper}>
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              style={styles.viewAsTrigger}
              aria-haspopup="listbox"
              aria-expanded={open}
              title="Switch the active demo user persona"
            >
              <span style={styles.viewAsLabel}>View as {active?.label ?? '—'}</span>
              <span style={styles.viewAsChevron}>{open ? '▴' : '▾'}</span>
            </button>
            {open && (
              <ul role="listbox" style={styles.viewAsMenu}>
                {viewAsUsers.map((u) => {
                  const isSelected = u.key === viewAsKey;
                  return (
                    <li
                      key={u.key}
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => {
                        onViewAsChange(u.key);
                        setOpen(false);
                      }}
                      style={{
                        ...styles.viewAsOption,
                        ...(isSelected ? styles.viewAsOptionSelected : {}),
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = isSelected ? '#dbeafe' : '#f1f5f9';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = isSelected ? '#eff6ff' : 'white';
                      }}
                    >
                      {u.label}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {onProfile && (
            <button
              onClick={onProfile}
              style={styles.profileButton}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#475569')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#334155')}
            >
              User Profile
            </button>
          )}
          {onLogout && (
            <button
              onClick={onLogout}
              style={styles.logoutButton}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#dc2626')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ef4444')}
            >
              Logout
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

const styles: Record<string, React.CSSProperties> = {
  header: {
    backgroundColor: '#1e293b',
    color: 'white',
    padding: '1rem 2rem',
    borderBottom: '2px solid #3b82f6',
  },
  container: {
    maxWidth: '1600px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  leftSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  viewAsWrapper: {
    position: 'relative',
  },
  viewAsTrigger: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.5rem 0.85rem',
    backgroundColor: '#0f172a',
    color: 'white',
    border: '1px solid #475569',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  viewAsLabel: { whiteSpace: 'nowrap' },
  viewAsChevron: { fontSize: '0.7rem', opacity: 0.8 },
  viewAsMenu: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    right: 0,
    minWidth: 180,
    margin: 0,
    padding: 0,
    listStyle: 'none',
    backgroundColor: 'white',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
    zIndex: 30,
  },
  viewAsOption: {
    padding: '0.55rem 0.75rem',
    cursor: 'pointer',
    backgroundColor: 'white',
    transition: 'background-color 0.1s',
    fontSize: '0.85rem',
    color: '#0f172a',
  },
  viewAsOptionSelected: { backgroundColor: '#eff6ff', fontWeight: 600 },
  profileButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#334155',
    color: 'white',
    border: '1px solid #475569',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  title: {
    margin: 0,
    fontSize: '1.5rem',
    fontWeight: 600,
  },
  subtitle: {
    margin: '0.25rem 0 0 0',
    fontSize: '0.875rem',
    opacity: 0.8,
  },
  logoutButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
};
